import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { MarketCode } from '@tink/config/src/markets';
import { UseGradualRollout } from '../../../../../../utilities/useGradualRollout';
import { SetTimer, timerUtils } from '../../../../../../utilities/timer';
import { applicationParameters } from '../../../../../../utilities/application-parameters';
import { AppState } from '../../../../../../stores/redux-store';
import {
  LoadingOverlay as LoadingOverlayImport,
  LoadingOverlayProps,
} from '../../../../../../core/screens/LoadingOverlay/LoadingOverlay';
import {
  InteractionElement,
  parentMessenger,
} from '../../../../../../core/Analytics/ParentApplicationEventMessenger';
import { useAnalytics } from '../../../../../../core/Analytics/AnalyticsProvider';
import { ProviderEntity } from '../../../../../../models/provider';
import { CredentialsEntity, CredentialsStatus } from '../../../../../../models/credentials';
import { setRedirectedCredentialsId as setRedirectedCredentialsIdConnect } from '../../../../../../stores/app-state-store';
import { PageView, UserInteractionEvent } from '../../../../../../models/analytics';
import {
  ThirdpartyRedirectorInterface,
  UseThirdPartyRedirector,
  useThirdPartyRedirector as useThirdPartyRedirectorImport,
} from '../../../../../../utilities/thirdparty-redirector/thirdparty-redirector';
import { useConfiguration } from '../../../../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { shouldAutoRedirectMobileDeviceToThirdParty } from '../../../../../../models/configuration';
import {
  AwaitingTppPendingNoAutoRedirectScreen as AwaitingTppPendingNoAutoRedirectScreenImport,
  AwaitingTppPendingScreenNoAutoRedirectProps,
} from '../../AwaitingMobileBankIdAuthenticationContainer/molecules/PendingThirdPartyAuthentication/AwaitingTppPendingScreen';
import { stopPollingCredentials as stopPollingCredentialsImport } from '../../../../../../connectivity/services/credentials';
import { resetCredentials as resetCredentialsConnect } from '../../../../../../stores/credentials-store';
import {
  AwaitingTppInstructionScreen as AwaitingTppInstructionScreenImport,
  AwaitingTppInstructionScreenProps,
} from '../../../AwaitingTppInstructionScreen';
import {
  AwaitingTppRedirectScreen as AwaitingTppRedirectScreenImport,
  AwaitingTppRedirectScreenProps,
} from '../../../AwaitingTppRedirectScreen';
import { shouldAutoRedirectSecondSCAToThirdParty } from '../../../../../../connectivity/screens/third-party-redirect/thirdPartyRedirectHelpers';
import { BackButton } from '../../../../../organisms/Header/HeaderMolecule';
import { getAutoRedirectMobileQueryParamAnalytic } from './getAutoRedirectMobileQueryParamAnalytic';

export type ShouldAutoRedirectToThirdPartyArguments = {
  thirdPartyRedirector?: ThirdpartyRedirectorInterface;
  isDesktop: boolean;
  configuration: ConfigurationModel;
};

export const shouldAutoRedirectToThirdParty = ({
  isDesktop,
  thirdPartyRedirector,
  configuration,
}: ShouldAutoRedirectToThirdPartyArguments) => {
  const isDeviceSuitable =
    isDesktop ||
    shouldAutoRedirectMobileDeviceToThirdParty(configuration) ||
    Boolean(applicationParameters.get('autoRedirectMobile'));
  const isNotUsMarket = applicationParameters.get('market') !== MarketCode.US;

  return (
    thirdPartyRedirector?.shouldNavigateByRedirect() &&
    (isDeviceSuitable || shouldAutoRedirectSecondSCAToThirdParty(configuration)) &&
    isNotUsMarket
  );
};

export type AuthAwaitingThirdpartyAuthenticationContainerOwnProps = {
  clientName?: string;
  credentials: CredentialsEntity<CredentialsStatus.AWAITING_THIRD_PARTY_APP_AUTHENTICATION>;
  provider: ProviderEntity;
  isDesktop?: boolean;
  setTimer?: SetTimer;
  useThirdPartyRedirector?: UseThirdPartyRedirector;
  stopPollingCredentials?: typeof stopPollingCredentialsImport;
  LoadingOverlay?: React.ComponentType<LoadingOverlayProps>;
  AwaitingTppInstructionScreen?: React.ComponentType<AwaitingTppInstructionScreenProps>;
  AwaitingTppPendingScreen?: React.ComponentType<AwaitingTppPendingScreenNoAutoRedirectProps>;
  AwaitingTppRedirectScreen?: React.ComponentType<AwaitingTppRedirectScreenProps>;
  isClientVerified: boolean;
  useGradualRollout?: UseGradualRollout;
} & BackButton;

export type AuthAwaitingThirdpartyAuthenticationContainerProps =
  AuthAwaitingThirdpartyAuthenticationContainerOwnProps & PropsFromRedux;

export const AuthAwaitingThirdpartyAuthenticationContainerPresenter: React.FC<
  AuthAwaitingThirdpartyAuthenticationContainerProps
> = ({
  clientName,
  credentials,
  provider,
  setRedirectedCredentialsId,
  setTimer = timerUtils.setTimer,
  isDesktop = userAgent.isDesktop,
  useThirdPartyRedirector = useThirdPartyRedirectorImport,
  resetCredentials = resetCredentialsConnect,
  stopPollingCredentials = stopPollingCredentialsImport,
  LoadingOverlay = LoadingOverlayImport,
  AwaitingTppPendingScreen = AwaitingTppPendingNoAutoRedirectScreenImport,
  AwaitingTppInstructionScreen = AwaitingTppInstructionScreenImport,
  AwaitingTppRedirectScreen = AwaitingTppRedirectScreenImport,
  onBack,
  isClientVerified,
}) => {
  const { tinklinkAnalytics } = useAnalytics();
  const history = useHistory();
  const [awaitingAuth, setAwaitingAuth] = useState(false);
  const { data } = useConfiguration();
  const { thirdPartyRedirector } = useThirdPartyRedirector(credentials);
  const shouldAutoRedirect = shouldAutoRedirectToThirdParty({
    isDesktop,
    configuration: data,
    thirdPartyRedirector,
  });
  const providerDisplayName = provider.displayName;
  const shouldShowConsentRedirectScreen = !isDesktop;

  const onBackPendingThirdPartyButton = () => {
    resetCredentials();
    stopPollingCredentials();
    history.goBack();
  };

  const onClickHandler = useCallback(() => {
    const userInteractionEvent = UserInteractionEvent.PRIMARY_BUTTON;
    const interactionElement = InteractionElement.PRIMARY_BUTTON;

    tinklinkAnalytics.sendInteractionEvent({
      action: `${PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN}/${userInteractionEvent}`,
      view: PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN,
    });

    parentMessenger.fireInteractionEvent({
      pageView: PageView.AWAITING_THIRD_PARTY_AUTHENTICATION_SCREEN,
      element: interactionElement,
    });
    setRedirectedCredentialsId(credentials.id);

    setTimer(() => {
      setAwaitingAuth(true);
    }, 500);

    thirdPartyRedirector?.navigate();
  }, [
    credentials.id,
    setRedirectedCredentialsId,
    thirdPartyRedirector,
    tinklinkAnalytics,
    setTimer,
  ]);

  useEffect(() => {
    if (shouldAutoRedirect) {
      setRedirectedCredentialsId(credentials.id);
      thirdPartyRedirector?.navigate();

      setTimer(() => {
        // Show "PendingThirdPartyAuthentication" screen to allow user navigate to bank manually if automatic redirect fails.
        setAwaitingAuth(true);
      }, 6000);
    }
  }, [
    credentials.id,
    setRedirectedCredentialsId,
    shouldAutoRedirect,
    thirdPartyRedirector,
    setTimer,
  ]);

  useEffect(() => {
    const interactionEvent = getAutoRedirectMobileQueryParamAnalytic();

    if (interactionEvent) tinklinkAnalytics.sendInteractionEventOnce(interactionEvent);
  }, [tinklinkAnalytics]);

  if (shouldAutoRedirect && !awaitingAuth) {
    return <LoadingOverlay />;
  }

  if (awaitingAuth) {
    return (
      <AwaitingTppPendingScreen
        providerName={providerDisplayName}
        onBack={onBack || onBackPendingThirdPartyButton}
        onClick={onClickHandler}
        credentials={credentials}
      />
    );
  }

  if (shouldShowConsentRedirectScreen) {
    return (
      <AwaitingTppRedirectScreen
        provider={provider}
        clientName={clientName || ''}
        onBack={onBack}
        isVerified={isClientVerified}
        onClick={onClickHandler}
      />
    );
  }

  return (
    <AwaitingTppInstructionScreen
      provider={provider}
      clientName={clientName || ''}
      onBack={onBack}
      isVerified={isClientVerified}
      onClick={onClickHandler}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isClientVerified: Boolean(state.oauth.description?.verified),
    userId: state.user.user?.id,
  };
};

const mapDispatchToProps = {
  setRedirectedCredentialsId: setRedirectedCredentialsIdConnect,
  resetCredentials: resetCredentialsConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const AuthAwaitingThirdpartyAuthenticationContainer = connector(
  AuthAwaitingThirdpartyAuthenticationContainerPresenter
);
